// ImageWithLoaderCancel.jsx
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "next-themes";

/**
 * ImageWithLoaderCancel
 * - Cancels the original image download on timeout
 * - If fallbackSrc is provided, it loads the fallback after cancelling original
 * - Otherwise shows error UI (no partially-loading image)
 *
 * Props:
 *  - src (string)           : required main image url
 *  - fallbackSrc (string)   : optional fallback image to show on timeout/error
 *  - timeoutMs (number)     : ms to wait before cancelling (default 45s)
 *  - loading ("eager"|"lazy"): default "eager" so browser starts fetch immediately
 *  - decoding, fetchPriority, placeholder, style, etc. forwarded to <img>
 */
export default function ImageWithLoader({
  src,
  fallbackSrc = null,
  alt = "",
  timeoutMs = 45000,
  loading = "eager",
  decoding = "async",
  fetchPriority,
  placeholder = null,
  className = "",
  imgClassName = "",
  style = {},
  ...imgProps
}) {
  const imgRef = useRef(null);
  const timerRef = useRef(null);
  const cancelledRef = useRef(false); // marks we explicitly cancelled the original download

  const [loadingState, setLoadingState] = useState(true);
  const [errored, setErrored] = useState(false);
  const [usingFallback, setUsingFallback] = useState(false);

  const { t } = useTranslation();
  const { theme } = useTheme();

  // compute which src the <img> should show right now:
  // - if we've switched to fallback, use fallbackSrc
  // - otherwise show original src (unless cancelled and no fallback, in which case we'll clear src)
  const effectiveSrc = usingFallback && fallbackSrc ? fallbackSrc : src;

  useEffect(() => {
    // reset states when src changes
    cancelledRef.current = false;
    setUsingFallback(false);
    setErrored(false);
    setLoadingState(true);

    // safety: clear prior timer
    clearTimeout(timerRef.current);

    // start timeout to cancel original download if too slow
    timerRef.current = setTimeout(() => {
      // only do cancellation if still loading
      if (imgRef.current && loadingState) {
        // mark cancelled to avoid handling later onLoad/onError from the original request
        cancelledRef.current = true;

        // remove event handlers to avoid races
        try {
          imgRef.current.onload = null;
          imgRef.current.onerror = null;
        } catch (e) {
          // ignore
        }

        // Cancel the native download by clearing the src.
        // Setting to empty string or about:blank typically aborts the current request.
        try {
          imgRef.current.src = ""; // abort original fetch
        } catch (e) {
          // ignore
        }

        // If a fallback is provided: switch to fallback and start loading it.
        if (fallbackSrc) {
          setUsingFallback(true);
          // set the fallback src directly on the <img> element so it starts fetching immediately
          try {
            imgRef.current.src = fallbackSrc;
          } catch (e) {
            // ignore
          }
          // Keep loadingState true while fallback loads; errored remains false until fallback fails
          setLoadingState(true);
          setErrored(false);
        } else {
          // No fallback: treat this as a final error and show error UI (no image)
          setLoadingState(false);
          setErrored(true);
        }
      }
    }, timeoutMs);

    return () => {
      clearTimeout(timerRef.current);
      // don't leak handlers
      if (imgRef.current) {
        try {
          imgRef.current.onload = null;
          imgRef.current.onerror = null;
        } catch (e) {}
      }
    };
    // we intentionally depend on src and timeoutMs
  }, [src, fallbackSrc, timeoutMs]);

  // NOTE: assign handlers on the DOM <img> element via props (React will set them),
  // but our handlers check cancelledRef to ignore events from the cancelled original download.

  function handleLoad() {
    // If we cancelled the original but the fallback loaded, cancelledRef may be true but fallback is deliberate.
    // We only ignore events if cancelledRef is true AND we are *not* usingFallback.
    const isFromCancelledOriginal = cancelledRef.current && !usingFallback;
    if (isFromCancelledOriginal) {
      // ignore this load (it came after we aborted)
      return;
    }

    // otherwise normal success
    clearTimeout(timerRef.current);
    setLoadingState(false);
    setErrored(false);
    cancelledRef.current = false;
  }

  function handleError() {
    // If original download fired onError after we cancelled, ignore it
    const isFromCancelledOriginal = cancelledRef.current && !usingFallback;
    if (isFromCancelledOriginal) {
      return;
    }

    clearTimeout(timerRef.current);

    // If we haven't tried fallback yet and fallback exists, switch to it now
    if (fallbackSrc && !usingFallback) {
      setUsingFallback(true);
      setLoadingState(true);
      setErrored(false);
      cancelledRef.current = false;
      // set img src to fallback to start loading it
      try {
        if (imgRef.current) imgRef.current.src = fallbackSrc;
      } catch (e) {}
      return;
    }

    // Otherwise final error
    setLoadingState(false);
    setErrored(true);
    cancelledRef.current = false;
  }

  return (
    <div
      className={className}
      style={{
        position: "relative",
        display: "block",
        width: "100%",
        minHeight: "150px",
        ...style,
      }}
    >
      {/* optional placeholder behind the image */}
      {placeholder && (
        <div
          aria-hidden
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            pointerEvents: "none",
            overflow: "hidden",
          }}
        >
          {placeholder}
        </div>
      )}

      {/* Render the native image element (keeps browser optimizations) */}
      {/* If we've cancelled without fallback, effectiveSrc may be empty; in that case the <img> shows nothing. */}
      <img
        ref={imgRef}
        src={effectiveSrc || ""}
        alt={alt}
        onLoad={handleLoad}
        onError={handleError}
        loading={loading}
        decoding={decoding}
        fetchPriority={fetchPriority}
        className={imgClassName}
        style={{
          display: effectiveSrc ? "block" : "none", // hide <img> visually if there is no src to avoid showing partial/blank
          width: "100%",
          height: "auto",
		  maxHeight: '600px',
          objectFit: imgProps.objectFit ?? "cover",
          opacity: loadingState ? 0.01 : 1, // visually hide while loading but let browser fetch
          transition: "opacity 200ms ease",
        }}
        {...imgProps}
      />

      {/* Loader overlay while loading (for both main image and fallback) */}
      {loadingState && !errored && (
        <div
          aria-hidden
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            pointerEvents: "none",
          }}
        >
          <svg width="36" height="36" viewBox="0 0 50 50" aria-hidden="true">
            <circle
              cx="25"
              cy="25"
              r="20"
              fill="none"
              strokeWidth="5"
              stroke="#888"
              strokeLinecap="round"
              strokeDasharray="31.415, 31.415"
            >
              <animateTransform attributeName="transform" type="rotate" from="0 25 25" to="360 25 25" dur="1s" repeatCount="indefinite" />
            </circle>
          </svg>
        </div>
      )}

      {/* Final error UI (only shown when we have a real error and no fallback active) */}
      {!loadingState && errored && !usingFallback && (
        <div
          className="rounded p-5 text-center text-danger"
          style={{
            backgroundColor: theme === "dark" ? "rgb(71, 71, 71)" : "",
            border: "1px solid",
            borderColor: theme === "dark" ? "rgb(64, 61, 64)" : "",
            overflow: "hidden",
          }}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width={24} height={24} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round" className="icon icon-tabler icons-tabler-outline icon-tabler-alert-square-rounded">
            <path stroke="none" d="M0 0h24v24H0z" fill="none" />
            <path d="M12 3c7.2 0 9 1.8 9 9s-1.8 9 -9 9s-9 -1.8 -9 -9s1.8 -9 9 -9z" />
            <path d="M12 8v4" />
            <path d="M12 16h.01" />
          </svg>
          <p className="m-0" style={{ color: theme === "dark" ? "rgb(171,171,171)" : "rgb(101,101,101)" }}>
            {t ? t("modals.request.failed") ?? "Failed to load" : "Failed to load"}
          </p>
        </div>
      )}
    </div>
  );
}
